import * as React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useQuery } from '@apollo/client/react'
// components
import { Header } from 'components/Modals/PlanItems/styles'
import { DrawerModal, LoaderWithOverlay } from 'components/UI'
// view components
import TextFieldModal from 'components/Modals/TextFieldModal'
import InlineFunction from 'components/PageComponents/RiskTemplates/Edit/components/RiskTemplateFields/components/Table/components/TableForm/components/Rows/components/Row/components/InlineFunctions/components/InlineFunction'
import RiskFieldsForm from './components/Form'
import RiskMatrixModal from 'components/Modals/MatrixModal'
// hooks
import { useFormInitialValues, useModalData, useRiskNotifications } from 'hooks/index'
// constants
import { Modals } from 'constants/index'
// types
import { IRiskFieldValue } from 'types/risk'
import {
  IRisk,
  IField,
  IRiskView,
  IFieldLink,
  IFieldValueMapItem,
  IGetGroupedFormsPermissionsResponse
} from 'types/graphql'
// themes
import { CloseIcon } from 'components/UI/Icons'
// views
import { FunctionsWrapper, Wrapper } from './views'
// context
import { RiskViewAPIContext } from 'containers/Risks/RiskView/layers/API'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// graphql
import { GROUPED_FORMS_PERMISSIONS_QUERY } from 'graphql/risk/queries'
// utils
import { isEmpty, omit, orderBy, sortBy } from 'lodash'

type GetGroupedFormsPermissions = {
  getGroupedFormsPermissions: IGetGroupedFormsPermissionsResponse
}

export const ImportedRiskContext = React.createContext<{
  importedRisk: IRisk | null
  isImportedField: boolean
  canModifyImportedFields: boolean
}>({
  importedRisk: null,
  isImportedField: false,
  canModifyImportedFields: false
})

const ImportedRiskModal: React.FC = () => {
  const router = useRouter()

  const {
    hideModal,
    modalData: { isOpen, riskView, risk }
  } = useModalData<{ risk: IRisk; riskView: IRiskView }>({ modalType: Modals.IMPORTED_RISK_MODAL })

  const { isLoading } = React.useContext(RiskTemplateAPIContext)
  const { risks, updateRisks } = React.useContext(RiskViewAPIContext)

  const { data: formsPermissionsQuery } = useQuery<GetGroupedFormsPermissions>(GROUPED_FORMS_PERMISSIONS_QUERY, {
    skip: !isOpen || !risk?.realRiskId,
    variables: {
      riskId: risk?.realRiskId
    }
  })

  const { inlineFunctions } = useRiskNotifications({
    riskId: risk?.realRiskId,
    riskTemplateId: risk?.riskTemplate.id,
    isImportedRisk: true
  })

  const { initialValues } = useFormInitialValues(isLoading, isOpen ? risk?.fieldValueMap || [] : [])

  const onFieldUpdate = React.useCallback(
    (field: IField, fieldValue: IRiskFieldValue) => {
      const updatedRisks = risks.map((item) => {
        if (item.id === risk.id) {
          const isNew = item.fieldValueMap.every(({ fieldValueId }) => fieldValueId !== fieldValue.fieldValueId)

          return {
            ...item,
            fieldValueMap: isNew
              ? [
                  ...item.fieldValueMap,
                  {
                    ...omit(fieldValue, 'value'),
                    fieldId: field.id,
                    fieldName: field.name,
                    fieldType: field.fieldType,
                    createdAt: new Date().toISOString(),
                    fieldValue: fieldValue.value
                  }
                ]
              : item.fieldValueMap.map((fv) => {
                  if (fv.fieldValueId === fieldValue.fieldValueId) {
                    return { ...fv, fieldValue: fieldValue.value }
                  }

                  return fv
                })
          }
        }

        return item
      })

      updateRisks(updatedRisks)
    },
    [risks, risk]
  )

  if (!isOpen) {
    return null
  }

  const groupedFormsPermissions = formsPermissionsQuery?.getGroupedFormsPermissions?.groupedFormsPermissionsMap

  const canModifyRisk = risk?.permissions?.riskModify && risk?.canUpdateFromSystemProcesses

  const canModifyImportedFields = groupedFormsPermissions?.find(({ primaryFieldValueId }) =>
    risk.fieldValueMap.some(
      (fieldValue) =>
        primaryFieldValueId === fieldValue.fieldValueId || primaryFieldValueId === fieldValue.parentFieldValueId
    )
  )?.permissions?.modifyOwnedGroupedRisks

  const primaryFieldLink: IFieldLink = risk.riskTemplate.fieldLinks.find(
    (fieldLink: IFieldLink) =>
      fieldLink.primary && risk.fieldValueMap.some((item: IFieldValueMapItem) => item.fieldLinkId === fieldLink.id)
  )

  const relatedFieldLinks: IFieldLink[] = risk.riskTemplate.fieldLinks.filter(
    (item: IFieldLink) => item.parentFieldId === primaryFieldLink?.parentFieldId
  )

  const fields: IField[] = sortBy(
    riskView.riskTemplate.fields
      .filter((field: IField) => relatedFieldLinks.some((fieldLink: IFieldLink) => fieldLink.fieldId === field.id))
      .map((item: IField) => ({
        ...item,
        position: relatedFieldLinks.find((fieldLink: IFieldLink) => fieldLink.fieldId === item.id)?.position
      })),
    'position'
  )

  const functionsToShow = orderBy(
    inlineFunctions?.filter((fn) => risk?.fieldValueMap.some((item) => item.fieldValueId === fn.fieldValueId)) || [],
    'position'
  )

  const preparedInitialValues = Object.fromEntries(
    fields.map((field) => {
      const fieldValue = risk.fieldValueMap.find((item: IFieldValueMapItem) => item.fieldId === field.id)
      const fieldLink = relatedFieldLinks.find((item) => item.fieldId === field.id)
      const parentFieldValueId =
        fieldValue?.parentFieldValueId ||
        (fieldLink.primary
          ? null
          : risk.fieldValueMap.find((item: IFieldValueMapItem) => item.fieldId === primaryFieldLink?.fieldId)
              ?.fieldValueId)

      const value = {
        value: initialValues[field.id]?.value ?? null,
        fieldLinkId: fieldLink?.id,
        parentFieldValueId,
        ...(fieldValue?.fieldValueId && { fieldValueId: fieldValue.fieldValueId })
      }

      return [field.id, value]
    })
  )

  const onCloseModal = () => {
    hideModal()

    if (router.query.riskId) {
      router.replace('/forms', undefined, { shallow: true })
    }
  }

  return (
    <DrawerModal width='700px' isOpen={isOpen} hideModal={onCloseModal}>
      <Header>
        {riskView.riskTemplate.name}
        <CloseIcon onClick={onCloseModal} />
      </Header>
      <Wrapper>
        {isEmpty(initialValues) ? (
          <LoaderWithOverlay isLoading />
        ) : (
          <ImportedRiskContext.Provider value={{ isImportedField: true, importedRisk: risk, canModifyImportedFields }}>
            <RiskFieldsForm
              fields={fields as any}
              onFieldUpdate={onFieldUpdate}
              initialValues={preparedInitialValues}
            />
            {(canModifyImportedFields || canModifyRisk) && (
              <FunctionsWrapper>
                {functionsToShow.map((item) => (
                  <InlineFunction
                    data={item}
                    key={item.id}
                    riskId={risk?.realRiskId}
                    riskTemplateId={risk?.riskTemplate.id}
                  />
                ))}
              </FunctionsWrapper>
            )}
          </ImportedRiskContext.Provider>
        )}
        <RiskMatrixModal />
        <TextFieldModal />
      </Wrapper>
    </DrawerModal>
  )
}

export default ImportedRiskModal
